import {z} from "zod";

import {Meta} from "@/src/modules/core/types";

import {Property} from "../../properties/types";
import {User} from "../../users/types";
import {adminActionSchema, bulkCopySchema, shiftSchema, templateSchema} from "../schemas";

// ADMIN ACTION
export type AdminActionSchema = z.infer<typeof adminActionSchema>;

export type AdminActionRequest = {
  action_type: string;
  from_datetime: string;
  to_datetime: string;
  reason: string;
  notes: string;
  billing_impact: boolean;
};

// BULK COPY
export type BulkCopySchema = z.infer<typeof bulkCopySchema>;

export type CopyRequest = {
  shift_id: number;
};

export type BulkCopyRequest = {
  source_from: string;
  source_to: string;
  target_from: string;
  target_to: string;
};

export type ScheduleRequest = {
  shift_id: number;
};

export type BulkScheduleRequest = {
  source_from: string;
  source_to: string;
};

export type BulkCopyResponse = {
  data: {
    copied_shifts: number;
    skipped_overlaps: number;
    total_processed: number;
    errors: string[];
  };
};

// SHIFT
export enum ShiftType {
  NORMAL = "normal",
  PATROL = "patrol",
  DISPATCH = "dispatch",
}

/** From shift resources: company vehicle condition cycle for patrol (and N/A otherwise). */
export type VehicleConditionCycleStatus =
  | "not_applicable"
  | "pending"
  | "started"
  | "ended";
export type ShiftSchema = z.infer<typeof shiftSchema>;

export type ShiftRequest = Omit<
  z.infer<typeof shiftSchema>,
  | "properties"
  | "patrol_route"
  | "from_date"
  | "from_time"
  | "to_date"
  | "to_time"
  | "breaks"
  | "break_suggestion"
  | "days"
  | "type"
  | "user"
  | "role_id"
> & {
  user_id: number | null;
  type: ShiftType;
  from_datetime: string;
  to_datetime: string;
  /** Omitted for patrol shifts (they carry `patrol_route_id` instead). */
  properties?: {
    property_id: number;
    assignment_type: "primary" | "secondary";
    shift_property_id?: number;
  }[];
  /** Patrol shifts only: send on create or when the route changed (re-snapshots visits). */
  patrol_route_id?: number;
  breaks: {
    shift_break_id?: number;
    break_type: "rest" | "lunch";
    start_datetime: string;
    is_paid: 1 | 0;
    duration_minutes: number;
  }[];
  notify_officer: boolean;
  extended_dates?: {from_datetime: string; to_datetime: string}[];
};

export interface ShiftProperty {
  assignment_type: "primary" | "secondary";
  shift_property_id: number;
  property: {
    id: number;
    name: string;
  };
}

export type ShiftPatrolVisit = {
  shift_patrol_visit_id: number;
  property: {
    property_id: number;
    name: string;
    /** Destination for navigation; from GET shifts / active-shift. */
    gps_coordinates?: string | null;
  };
  sort_order: number;
  suggested_start_at: string | null;
  suggested_end_at: string | null;
  duties: string | null;
  source: "planned" | "manual";
  checked: boolean;
  checked_at: string | null;
  gps_coordinates: string | null;
  gps_source: string | null;
  checked_by_user_id: number | null;
};

/** POST /shift/{id}/patrol-visit/{visitId}/check — device GPS only. */
export type CheckPatrolVisitRequest = {
  gps_coordinates: string;
  gps_source: "device";
};

/** POST /shift/{id}/patrol-visit — append a manual stop. */
export type AddPatrolVisitRequest = {
  property_id: number;
  duties?: string | null;
};

export type WorkedShift = {
  /** Primary key of the worked shift record (used by vehicle condition checks, etc.). */
  id?: number;
  worked_shift_id?: number;
  actual_start_datetime: string;
  actual_end_datetime: string | null;
};

export type AdminAction = {
  action_datetime: string;
  action_source: string;
  action_type: string;
  performed_by: string;
  notes: string;
  reason: string;

  original_planned_start_datetime: string;
  original_planned_end_datetime: string;
  admin_adjusted_planned_start_datetime: string;
  admin_adjusted_planned_end_datetime: string;

  original_officer_start_datetime: string;
  original_officer_end_datetime: string;
  admin_adjusted_start_datetime: string;
  admin_adjusted_end_datetime: string;
};

export type ScheduledBreak = {
  status: "scheduled";
  execution_start_datetime: string;
  execution_end_datetime: null;
};

export type ActiveBreak = {
  status: "active";
  execution_start_datetime: string;
  execution_end_datetime: string | null;
};

export type CompletedBreak = {
  status: "completed";
  execution_start_datetime: string;
  execution_end_datetime: string;
};

export type RejectedBreak = {
  status: "rejected";
  execution_start_datetime: string;
  execution_end_datetime: null;
};

export type CanceledBreak = {
  status: "canceled";
  execution_start_datetime: null;
  execution_end_datetime: null;
};

export type ShiftBreak = {
  shift_break_id: number;
  break_type: "rest" | "lunch";
  start_datetime: string;
  is_paid: 1 | 0;
  duration_minutes: number;
  delayed_count: number;
  interrupted_count: number;
  can_reject: 1 | 0;
  is_take_disabled: 1 | 0;
} & (ScheduledBreak | ActiveBreak | CompletedBreak | RejectedBreak | CanceledBreak);

export type Shift = {
  shift_id: number;
  /** Present when the API flattens the worked-shift id (see `getWorkedShiftId`). */
  worked_shift_id?: number;
  type: ShiftType;
  status: "unscheduled" | "scheduled" | "active" | "completed" | "cancelled" | "no_show";
  from_datetime: string;
  to_datetime: string;
  extended_end_datetime: string | null;
  created_at: string;
  updated_at: string;
  is_past_due: boolean;
  has_admin_taken_action: boolean;
  notes?: string;
  /** Present when the list API includes creator info. */
  created_by?: {
    id: number;
    name: string;
  };
  user: {
    id: number;
    name: string;
  } | null;
  worked_shift: WorkedShift;
  /**
   * From GET /shifts and shift-by-id. Replaces `requires_start_vehicle_condition_check`.
   * `pending` → show start check; `started` → require end check before/during end shift; `ended` / `not_applicable` → no modals.
   */
  vehicle_condition_cycle_status?: VehicleConditionCycleStatus;
  /**
   * Company vehicles used on this shift (start checks), ordered by first start per vehicle in the
   * worked shift; **last entry = currently held**. Empty for non-patrol or no start checks.
   * With meta-data, rows may include `unit_number`, `plate`, etc. for display (see formatPatrolVehicleDisplay).
   */
  company_vehicle?: {
    id: number;
    name?: string | null;
    unit_number?: string | null;
    plate?: string | null;
    make?: string | null;
    model?: string | null;
  }[];
  /** @deprecated Use `vehicle_condition_cycle_status === "pending"`. */
  requires_start_vehicle_condition_check?: boolean | 0 | 1;
  properties: ShiftProperty[];
  /** Patrol shifts routed to a patrol route. Absent/null for non-patrol or legacy route-less patrol shifts. */
  patrol_route?: {patrol_route_id: number; name: string} | null;
  /** Snapshot of the route's visits for this shift (patrol only). */
  patrol_visits?: ShiftPatrolVisit[];
  admin_actions: AdminAction[];
  breaks: ShiftBreak[];
};

export type GetShiftsResponse = {
  data: Shift[];
  meta: Meta;
};

export type ShiftCoverageProperty = {
  property_id: number;
  name: string;
  site_telephone?: string | null;
  assignment_type: "primary" | "secondary";
  sort_order: number;
  latitude: number;
  longitude: number;
};

export type ShiftCoverageLastKnown = {
  latitude: number;
  longitude: number;
  recorded_at: string;
};

export type ShiftCoverageBreakItem = {
  shift_break_id: number;
  break_type: "rest" | "lunch";
  start_datetime: string;
  duration_minutes: number;
  is_paid: boolean;
  is_overtime_break: boolean;
  execution_status: "scheduled" | "in_progress" | "completed" | "interrupted";
  taken: boolean;
  is_due: boolean;
  is_overdue: boolean;
  started_at: string | null;
  ended_at: string | null;
};

export type ShiftCoverageBreaks = {
  on_break: boolean;
  current: ShiftCoverageBreakItem | null;
  summary: {
    scheduled: number;
    completed: number;
    due: number;
    overdue: number;
  };
  items: ShiftCoverageBreakItem[];
};

export type ShiftCoverageRow = {
  shift_id: number;
  user_id: number;
  officer_name: string;
  telephone?: string | null;
  role?: {
    id: number;
    name: string;
  } | null;
  shift_type: ShiftType;
  status: "active";
  from_datetime: string;
  to_datetime: string;
  assigned: {
    properties: ShiftCoverageProperty[];
  };
  last_known: ShiftCoverageLastKnown | null;
  breaks?: ShiftCoverageBreaks;
};

export type ShiftCoverageMeta = {
  total: number;
  total_count?: number;
  resource_type?: string;
  api_version?: string;
  generated_at?: string;
};

export type GetShiftCoverageResponse = {
  success: 0 | 1;
  message: string;
  data: ShiftCoverageRow[];
  meta: ShiftCoverageMeta;
};

// TEMPLATE
export type TemplateSchema = z.infer<typeof templateSchema>;

export type TemplateRequest = Omit<z.infer<typeof templateSchema>, "properties" | "type"> & {
  type: ShiftType;
  properties: {
    property_id: number;
    assignment_type: "primary" | "secondary";
    template_property_id?: number;
  }[];
};

export type Template = Omit<TemplateSchema, "properties" | "type"> & {
  type: ShiftType;
  template_id: number;
  properties: {
    assignment_type: "primary" | "secondary";
    template_property_id: number;
    property: {
      id: number;
      name: string;
    };
  }[];
};

export type GetTemplatesResponse = {
  data: Template[];
  meta: Meta;
};

export type EmergencyShiftSchema = {
  type: {label: string; value: ShiftType} | null;
  properties: Property[];
  patrol_route: {id: number; name: string} | null;
  reason: string;
  from_time: string;
  to_time: string;
  from_date: Date;
  to_date: Date;
  gps_coordinates: string;
  gps_source?: import("@/src/modules/core/types/geolocation").GpsSource;
};

export type EmergencyShiftRequest = {
  type: ShiftType;
  properties?: {
    property_id: number;
    assignment_type: "primary" | "secondary";
  }[];
  /** Patrol emergency shifts only. */
  patrol_route_id?: number;
  from_datetime: string;
  to_datetime: string;
  reason: string;
  gps_coordinates: string;
  gps_source?: import("@/src/modules/core/types/geolocation").GpsSource;
};

// ACKNOWLEDGE SHIFT
export type AcknowledgeShiftRequest = {
  token: string;
  action: "acknowledge" | "decline";
};

export type AcknowledgeShiftResponse = {
  success: 0 | 1;
  message: string;
  data?: {
    successful: number[];
    failed: number[];
  };
  errors?: {
    token?: string[];
    action?: string[];
  };
};

export type ShiftFilters = {
  id?: number;
  view?: "weekly" | "monthly";
  properties?: Property[];
  officers?: User[];
  showAllProperties?: boolean;
};

export type {
  ShiftBreakExemptReason,
  ShiftBreakSuggestionSnapshot,
  ShiftBreakType,
  SuggestedShiftBreak,
  SuggestShiftBreaksData,
  SuggestShiftBreaksParams,
} from "./suggest-shift-breaks";
